'use client';

// Global
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { tv } from 'tailwind-variants';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { getTestProps } from 'lib/testing/utils';
import { Field, ImageField, LinkField } from '@sitecore-content-sdk/nextjs';
import { smoothScrollToElement } from 'lib/hooks/useScrollElementIntoView';
import { useReactToPrint } from 'react-to-print';
import PrintFees, { PrintFeesProps } from 'helpers/ConveyancingSteps/PrintFees';

export type MultiStepItem = {
  /**
   * Optional label shown above the title (e.g. "STEP 1").
   * If not provided, it will be derived from the index ("STEP n").
   */
  stepLabel?: Field<string>;
  /**
   * Main text for the step (e.g. "Tell us about you").
   */
  title?: Field<string>;
};

export type MultiStepProps = {
  /**
   * Zero-based index of the active step.
   */
  activeIndex?: number;
  /**
   * Optional function to render the content panel for the active step.
   * If provided, a content panel appears below the step header.
   */
  renderContent?: (args: { index: number; step: MultiStepItem }) => React.ReactNode;
  /**
   * Alternative to renderContent: array of nodes matching steps.
   * The element at active index will be rendered.
   * Each element can be a single ReactNode or an array of ReactNodes.
   * When an array is provided, tabs will be created using the `tabTitle` prop from each component.
   */
  contents?: Array<React.ReactNode | React.ReactNode[]>;
  /**
   * Navigation configuration for Back/Next buttons in the content panel.
   */
  nav?: {
    back?: {
      label?:
        | Field<string>
        | LinkField
        | ((args: { index: number }) => Field<string> | LinkField | undefined);
      hidden?: boolean;
      disabled?: boolean;
      onClick?: (args: {
        index: number;
        go: { prev: () => void; next: () => void; setIndex: (i: number) => void };
      }) => void;
    };
    next?: {
      label?: Field<string> | ((args: { index: number }) => Field<string> | undefined);
      hidden?: boolean;
      disabled?: boolean | ((args: { index: number }) => boolean);
      onClick?: (args: {
        index: number;
        go: { prev: () => void; next: () => void; setIndex: (i: number) => void };
      }) => void;
    };
    className?: string;
  };
  /**
   * Session storage key to persist the active step.
   * When provided and the component is uncontrolled, the active step
   * will be read from and written to sessionStorage.
   */
  storageKey?: string;
  /**
   * Initial step when no session value is found.
   */
  defaultActiveIndex?: number;
  /**
   * Reset key that when changed, will reset the step to defaultActiveIndex.
   * Useful for resetting when external conditions change (e.g., selectedOption).
   */
  resetKey?: string | number;
  /**
   * Steps to render in the helper.
   */
  steps: MultiStepItem[];
  /**
   * Optional extra className on the root.
   */
  className?: string;
  /**
   * Accessible label for the stepper container.
   */
  ariaLabel?: string;
  /**
   * Optional className for the content panel wrapper.
   */
  contentClassName?: string;
  /**
   * Called when active step changes (both controlled and uncontrolled flows).
   */
  onActiveIndexChange?: (newIndex: number, prevIndex: number) => void;
  /**
   * Enable clicking on steps to change the active step (uncontrolled only).
   * In controlled mode, clicking will call onActiveIndexChange with the index.
   */
  clickable?: boolean;
  ctaDescription?: {
    label?: Field<string> | ((args: { index: number }) => Field<string> | undefined);
    hidden?: boolean;
  };
  /**
   * Show or hide the print button. Defaults to true.
   */
  showPrintButton?: boolean | ((args: { index: number }) => boolean);
  printDescription?: Field<string>;
  printLogoIcon?: ImageField;
  printTitle?: Field<string>;
};

const stepContainer = tv({
  base: ['relative', 'flex', 'items-center', 'min-h-px', 'min-w-px', 'grow'],
});

const stepTextWrapper = tv({
  base: [
    'flex',
    'flex-col',
    'items-start',
    'justify-center',
    'min-w-px',
    'min-h-px',
    'w-full',
    'gap-padding-none',
  ],
});

const stepContent = tv({
  base: [
    'flex',
    'flex-col',
    'justify-center',
    'items-start',
    'min-h-px',
    'min-w-px',
    // responsive paddings/heights (compact on mobile/tablet, spacious on desktop)
    'px-[24px]',
    'py-[8px]',
    'lg:px-padding-base',
    'lg:py-padding-less',
    // fixed height on small screens to match mobile/tablet spec
    'h-[64px]',
    'lg:h-auto',
    // width follows Figma proportion but will shrink if needed
    'shrink-0',
    'grow',
  ],
  variants: {
    shape: {
      first: [
        'lg:rounded-bl-border-radius-radius-4',
        'lg:rounded-tl-border-radius-radius-4',
        'pl-padding-base',
        // triangular tip only on desktop
        'lg:before:content-[""]',
        'lg:before:absolute',
        'lg:before:top-0',
        'lg:before:right-0',
        'lg:before:bg-inherit',
        'lg:before:w-[36px]',
        'lg:before:h-full',
        'lg:before:[clip-path:polygon(0_0,100%_50%,0_100%)]',
      ],
      middle: [
        // tails/tips only on desktop
        'pl-padding-base',
        'lg:after:content-[""]',
        'lg:after:absolute',
        'lg:after:top-0',
        'lg:after:left-[-24px]',
        'lg:after:bg-inherit',
        'lg:after:w-[36px]',
        'lg:after:h-full',
        'lg:after:[clip-path:polygon(0%_0.001%,100%_0%,100%_50.001%,100%_100%,0%_100%,100%_50.001%,0%_0.001%)]',
        'lg:before:content-[""]',
        'lg:before:absolute',
        'lg:before:top-0',
        'lg:before:right-0',
        'lg:before:bg-inherit',
        'lg:before:w-[36px]',
        'lg:before:h-full',
        'lg:before:[clip-path:polygon(0_0,100%_50%,0_100%)]',
      ],
      last: [
        'lg:rounded-br-border-radius-radius-4',
        'lg:rounded-tr-border-radius-radius-4',
        'pl-padding-base',
        'pr-padding-less',
        // tail only on desktop
        'lg:after:content-[""]',
        'lg:after:absolute',
        'lg:after:top-0',
        'lg:after:left-[-24px]',
        'lg:after:bg-inherit',
        'lg:after:w-[36px]',
        'lg:after:h-full',
        'lg:after:[clip-path:polygon(0%_0.001%,100%_0%,100%_50.001%,100%_100%,0%_100%,100%_50.001%,0%_0.001%)]',
      ],
    },
    state: {
      inactive: ['bg-color-fill-light'],
      active: ['bg-color-fill-white', 'lg:shadow-[0px_0px_8px_0px_rgba(0,0,0,0.1)]', 'z-10'],
    },
  },
  defaultVariants: {
    state: 'inactive',
  },
});

const stepTextStep = tv({
  base: [
    'font-typography-font-family-body',
    'text-typography-font-size-body-medium',
    'leading-typography-line-height-body-medium',
    'font-semibold',
    'w-full',
    'whitespace-nowrap',
    'overflow-hidden',
    'text-ellipsis',
  ],
  variants: {
    state: {
      inactive: ['text-color-text-text'],
      active: ['text-color-core-color-4-skyblue-darkTint'],
    },
  },
  defaultVariants: {
    state: 'inactive',
  },
});

const stepTextTitle = tv({
  base: [
    'font-typography-font-family-body',
    'text-typography-font-size-body-large',
    'leading-typography-line-height-body-large',
    'font-normal',
    'w-full',
    'whitespace-nowrap',
    'overflow-hidden',
    'text-ellipsis',
    'text-color-text-dark',
  ],
});

const stepBlock = tv({
  base: [
    'flex',
    'flex-col',
    'lg:flex-row',
    'gap-stepper-stepper-gap',
    'rounded-border-radius-radius-4',
    'lg:rounded-none',
    'overflow-hidden',
    'lg:overflow-visible',
    'lg:shadow-none',
    'shadow-[0_0_8px_0_rgba(0,0,0,0.1)]',
  ],
});

const contentPanel = tv({
  base: [
    'w-full',
    'bg-color-surface-white',
    'shadow-[0_0_8px_0_rgba(0,0,0,0.1)]',
    'relative',
    'z-10',
  ],
  variants: {
    hasTabs: {
      true: ['rounded-tl-none', 'rounded-tr-none', 'md:rounded-tr-general-border-radius-card'],
      false: ['rounded-general-border-radius-card'],
    },
  },
});

const contentContainer = tv({
  base: ['mt-component-section-container-gutter'],
});

const tabsContainer = tv({
  base: ['flex', 'gap-spacing-spacing-4', 'items-start', 'relative'],
});

const tabButton = tv({
  base: [
    'flex',
    'gap-spacing-spacing-8',
    'items-center',
    'justify-center',
    'min-w-[50%]',
    'md:min-w-[210px]',
    'px-spacing-spacing-24',
    'py-spacing-spacing-16',
    'rounded-tl-border-radius-radius-2',
    'rounded-tr-border-radius-radius-2',
    'cursor-pointer',
    'font-typography-font-family-body',
    'text-typography-font-size-body-medium',
    'font-semibold',
    'leading-typography-line-height-xxsmall',
    'text-center',
    'transition-colors',
    'duration-200',
  ],
  variants: {
    isActive: {
      true: ['bg-color-core-color-1-Navy', 'text-color-grayscale-white'],
      false: ['bg-component-carousel-bg', 'text-component-tab-color-text'],
    },
  },
  defaultVariants: {
    isActive: false,
  },
});

const navBar = tv({
  base: [
    'w-full',
    'flex',
    'flex-col',
    'lg:flex-row',
    'items-center',
    'justify-between',
    'gap-spacing-spacing-16',
    'px-margin-base',
    'py-padding-less',
    'border-t-component-card-item-border-width',
    'border-solid',
    'border-t-component-Profile-Block-divider',
  ],
});

const ctaAreaBlock = tv({
  slots: {
    ctaLeft: [
      'flex',
      'flex-col',
      'lg:flex-row',
      'gap-spacing-spacing-16',
      'items-center',
      'w-full',
      'lg:w-auto',
    ],
    ctaButtons: [
      'flex',
      'gap-spacing-spacing-8',
      'p-0',
      'flex-wrap',
      'md:flex-nowrap',
      'justify-center',
      'w-full',
      'lg:w-auto',
    ],
    ctaText: [
      'flex',
      'flex-col',
      'items-center',
      'lg:items-start',
      'justify-center',
      'leading-0',
      'text-color-core-color-1-Navy',
      'text-typography-font-size-body-medium',
      'text-center',
      'lg:text-left',
    ],
    ctaInfo: [
      '[&>*]:!leading-typography-line-height-body-medium',
      '[&>*]:!text-typography-font-size-body-medium',
    ],
    ctaRight: ['flex', 'gap-padding-micro', 'items-center', 'justify-end', 'p-0'],
  },
});

const { ctaLeft, ctaButtons, ctaText, ctaInfo, ctaRight } = ctaAreaBlock();

/**
 * Small triangular connector rendered using a clipped block.
 * side: 'right' renders a pointer to the right, 'left' renders a tail from previous.
 * bg: background color for the triangle.
 */
function Triangle({
  side,
  bg,
  className,
  'data-node-id': dataNodeId,
  withShadow,
}: {
  side: 'left' | 'right';
  bg: string;
  className?: string;
  'data-node-id'?: string;
  /**
   * When true, apply a drop-shadow that respects the clipPath shape.
   */
  withShadow?: boolean;
}) {
  const clipPath =
    side === 'right'
      ? 'polygon(0 0, 100% 50%, 0 100%)'
      : 'polygon( 0% 0.001%,100% 0%,100% 50.001%,100% 100%,0% 100%,100% 50.001%,0% 0.001% )';
  return (
    <div
      data-node-id={dataNodeId}
      className={`${side === 'right' ? 'relative' : 'absolute -left-[25px]'} shrink-0 h-full w-[36px] ${className ?? ''}`}
      style={{
        filter: withShadow ? 'drop-shadow(0px 0px 8px rgba(0,0,0,0.1))' : undefined,
      }}
    >
      <div
        className={`${side === 'right' ? 'absolute' : 'relative h-full'} inset-0 ${bg ?? ''}`}
        style={{
          clipPath,
        }}
      />
    </div>
  );
}
export function useMultiStepSession(
  storageKey: string,
  options: { defaultActiveIndex?: number; stepsCount: number }
) {
  const { defaultActiveIndex = 0, stepsCount } = options;
  const clamp = useCallback(
    (idx: number) => Math.max(0, Math.min(stepsCount - 1, idx)),
    [stepsCount]
  );

  const readSession = useCallback(() => {
    if (typeof window === 'undefined') return defaultActiveIndex;
    try {
      const raw = window.sessionStorage.getItem(storageKey);
      if (raw == null) return defaultActiveIndex;
      const parsed = Number.parseInt(raw, 10);
      return Number.isFinite(parsed) ? clamp(parsed) : defaultActiveIndex;
    } catch {
      return defaultActiveIndex;
    }
  }, [storageKey, defaultActiveIndex, clamp]);

  const [index, setIndex] = useState<number>(defaultActiveIndex);

  // Initialize from session on mount
  useEffect(() => {
    setIndex(readSession());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readSession]);

  // Persist when changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.sessionStorage.setItem(storageKey, String(index));
    } catch {
      // ignore
    }
  }, [storageKey, index]);

  // Ensure index stays within bounds if stepsCount changes
  useEffect(() => {
    setIndex((prev) => clamp(prev));
  }, [clamp]);

  const update = useCallback(
    (next: number) => {
      setIndex(clamp(next));
    },
    [clamp]
  );
  const next = useCallback(() => {
    setIndex((prev) => clamp(prev + 1));
  }, [clamp]);
  const prev = useCallback(() => {
    setIndex((prev) => clamp(prev - 1));
  }, [clamp]);
  const reset = useCallback(() => {
    setIndex(clamp(defaultActiveIndex));
  }, [clamp, defaultActiveIndex]);

  return useMemo(
    () => ({ index, setIndex: update, next, prev, reset }),
    [index, update, next, prev, reset]
  );
}

export const MultiStep: React.FC<MultiStepProps> = (props) => {
  const {
    steps,
    activeIndex,
    storageKey,
    defaultActiveIndex = 0,
    resetKey,
    className,
    ariaLabel = 'Progress steps',
    contentClassName,
    onActiveIndexChange,
    clickable = false,
    renderContent,
    contents,
    nav,
    ctaDescription,
    showPrintButton = () => false,
    printDescription,
    printLogoIcon,
    printTitle,
  } = props;
  const lastIndex = steps.length - 1;
  const isControlled = typeof activeIndex === 'number';

  // Clamp current index for safety if steps change
  const clampIndex = useCallback(
    (idx: number) => Math.max(0, Math.min(lastIndex, idx)),
    [lastIndex]
  );

  // Uncontrolled state (with optional sessionStorage persistence)
  const [internalIndex, setInternalIndex] = useState<number>(clampIndex(defaultActiveIndex));

  // Ref for scrolling to top of content container
  const stepBlockRef = useRef<HTMLDivElement>(null);
  const printContentRef = useRef<HTMLDivElement>(null);

  const reactToPrintFn = useReactToPrint({
    contentRef: printContentRef,
    preserveAfterPrint: true,
    print: (printIframe) =>
      new Promise(() => {
        printIframe.style.display = 'none';
        printIframe.contentWindow?.print();
      }),
  });
  // Initialize from session on mount if storageKey is provided
  useEffect(() => {
    if (!storageKey || typeof window === 'undefined') return;
    try {
      const raw = window.sessionStorage.getItem(storageKey);
      if (raw == null) {
        setInternalIndex((prev) => clampIndex(prev));
        return;
      }
      const parsed = Number.parseInt(raw, 10);
      setInternalIndex(
        Number.isFinite(parsed) ? clampIndex(parsed) : clampIndex(defaultActiveIndex)
      );
    } catch {
      setInternalIndex((prev) => clampIndex(prev));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  // Persist uncontrolled changes to session
  useEffect(() => {
    if (!storageKey || typeof window === 'undefined') return;
    try {
      window.sessionStorage.setItem(storageKey, String(internalIndex));
    } catch {
      // ignore
    }
  }, [storageKey, internalIndex]);

  // Ensure internal index stays within bounds as steps change
  useEffect(() => {
    setInternalIndex((prev) => clampIndex(prev));
  }, [clampIndex]);

  // Reset to defaultActiveIndex only when resetKey changes (based on sessionStorage)
  useEffect(() => {
    if (isControlled) return;

    // Get previous resetKey from sessionStorage
    let prevResetKey: string | null = null;
    if (typeof window !== 'undefined') {
      try {
        prevResetKey = window.sessionStorage.getItem('conveyancing-reset-key');
      } catch {
        // ignore
      }
    }

    // Handle case when resetKey is undefined (no selected option)
    if (resetKey === undefined) {
      // Reset if there was a previous resetKey stored
      if (prevResetKey !== null) {
        setInternalIndex(clampIndex(defaultActiveIndex));
        // Clear storage when reset
        if (storageKey && typeof window !== 'undefined') {
          try {
            window.sessionStorage.setItem(storageKey, String(defaultActiveIndex));
            window.sessionStorage.removeItem('conveyancing-reset-key');
          } catch {
            // ignore
          }
        }
      }
      return;
    }

    const currentResetKey = String(resetKey);

    // Reset if resetKey changed OR if this is the first time selecting (prevResetKey is null)
    if (prevResetKey !== currentResetKey) {
      setInternalIndex(clampIndex(defaultActiveIndex));
      // Clear storage when reset
      if (storageKey && typeof window !== 'undefined') {
        try {
          window.sessionStorage.setItem(storageKey, String(defaultActiveIndex));
        } catch {
          // ignore
        }
      }
    }

    // Update sessionStorage with current resetKey
    if (typeof window !== 'undefined') {
      try {
        window.sessionStorage.setItem('conveyancing-reset-key', currentResetKey);
      } catch {
        // ignore
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey, isControlled, storageKey]);

  const currentIndex = isControlled ? clampIndex(activeIndex as number) : internalIndex;

  // Refs for back and next buttons to unfocus on step change
  const backButtonRef = useRef<HTMLButtonElement | null>(null);
  const nextButtonRef = useRef<HTMLButtonElement | null>(null);

  // Unfocus buttons when step changes
  useEffect(() => {
    if (backButtonRef.current) {
      backButtonRef.current.blur();
    }
    if (nextButtonRef.current) {
      nextButtonRef.current.blur();
    }
  }, [currentIndex]);

  const setCurrentIndex = useCallback(
    (next: number) => {
      const clamped = clampIndex(next);
      const prev = currentIndex;

      if (isControlled) {
        onActiveIndexChange?.(clamped, prev);
        return;
      }
      setInternalIndex(clamped);
      onActiveIndexChange?.(clamped, prev);
      // If neither session nor controlled, there's nothing to update; rely on parent props
    },
    [clampIndex, currentIndex, isControlled, onActiveIndexChange]
  );

  const onStepActivate = useCallback(
    (idx: number) => {
      setCurrentIndex(idx);
    },
    [setCurrentIndex]
  );

  const handlePrint = () => {
    reactToPrintFn();
  };

  const canGoPrev = currentIndex > 0;
  const canGoNext = currentIndex < lastIndex;
  const go = useMemo(
    () => ({
      prev: () => setCurrentIndex(currentIndex - 1),
      next: () => setCurrentIndex(currentIndex + 1),
      setIndex: (i: number) => setCurrentIndex(i),
    }),
    [currentIndex, setCurrentIndex]
  );

  // Extract content and tabs for current step
  const currentStepContent = contents?.[currentIndex];
  const isArrayContent = Array.isArray(currentStepContent);

  // Tab state - only used when tabs are provided
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);

  // Reset tab index when step changes
  useEffect(() => {
    setActiveTabIndex(0);
  }, [currentIndex]);

  // Extract tabs from array of ReactNodes (each should have tabTitle prop)
  type TabItem = {
    label: string;
    content: React.ReactNode;
    id: string;
  };

  const stepTabs = useMemo<TabItem[] | undefined>(() => {
    if (!isArrayContent || !Array.isArray(currentStepContent)) return undefined;

    return currentStepContent
      .filter((node) => React.isValidElement(node))
      .map((node, index) => {
        const props = (node as React.ReactElement).props as { tabTitle?: Field<string> };
        const tabTitle = props?.tabTitle;
        const label =
          typeof tabTitle === 'object' && tabTitle?.value ? tabTitle.value : `Tab ${index + 1}`;

        return {
          label,
          content: node,
          id: `tab-${index}`,
        };
      });
  }, [isArrayContent, currentStepContent]);

  const hasTabs = stepTabs && stepTabs.length > 1;

  // Get content to render
  const getContent = (): React.ReactNode => {
    if (renderContent) {
      return renderContent({ index: currentIndex, step: steps[currentIndex] });
    }

    if (hasTabs && stepTabs) {
      // Render all tab content, show/hide with CSS
      return (
        <>
          {stepTabs.map((tab, tabIndex) => {
            const isActive = tabIndex === activeTabIndex;
            return (
              <div
                key={tab.id || tabIndex}
                id={`tab-content-${tab.id || tabIndex}`}
                role="tabpanel"
                aria-labelledby={`tab-${tab.id || tabIndex}`}
                className={isActive ? 'block' : 'hidden'}
              >
                {tab.content}
              </div>
            );
          })}
        </>
      );
    }

    if (isArrayContent && Array.isArray(currentStepContent)) {
      // If array but no tabs (less than 2 items), render first item
      return currentStepContent[0] || null;
    }

    // Single ReactNode
    return currentStepContent ?? null;
  };

  const content = getContent();

  /**
   * Strips HTML tags from text while preserving the text content
   * @param html - The HTML string to strip tags from
   * @returns Plain text with all HTML tags removed
   */
  const stripHtmlTags = (html: string | undefined): string => {
    if (!html) return '';
    // Create a temporary DOM element to extract text content
    if (typeof window !== 'undefined' && typeof DOMParser !== 'undefined') {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      return doc.body.textContent || '';
    }
    // Fallback: use regex to remove HTML tags
    return html.replace(/<[^>]*>/g, '');
  };

  // Collect all tabs data for PrintFees when tabs are present
  const tabsDataForPrint = useMemo(() => {
    if (!hasTabs || !stepTabs) return undefined;

    return stepTabs
      .map((tab) => {
        const tabContent = tab.content;
        if (React.isValidElement(tabContent)) {
          const props = tabContent.props as PrintFeesProps;
          return {
            step2FeesExclVat: props?.step2FeesExclVat,
            step2MandatoryFees: props?.step2MandatoryFees,
            totalObj: props?.totalObj,
            tabTitle: props?.tabTitle,
            additionalFeesBlock: props?.additionalFeesBlock,
          };
        }
        return null;
      })
      .filter((tab): tab is NonNullable<typeof tab> => tab !== null);
  }, [hasTabs, stepTabs]);

  return (
    <>
      <div
        ref={stepBlockRef}
        className={stepBlock({ className })}
        role="list"
        aria-label={ariaLabel}
      >
        {steps.map((item, idx) => {
          const isActive = idx === currentIndex;
          const isFirst = idx === 0;
          const isLast = idx === lastIndex;

          const shape: 'first' | 'middle' | 'last' = isFirst ? 'first' : isLast ? 'last' : 'middle';

          return (
            <div
              key={`step-${idx}-${item.title?.value ?? ''}`}
              className={stepContainer()}
              role="listitem"
              aria-current={isActive ? 'step' : undefined}
            >
              {!isFirst && (
                <Triangle
                  side="left"
                  bg={isActive ? 'bg-color-fill-white' : 'bg-color-fill-light'}
                  data-node-id="tail"
                  withShadow={isActive}
                  className="hidden lg:block"
                />
              )}

              <div
                className={`${!isFirst ? 'lg:ml-[10px]' : ''} ${stepContent({
                  shape,
                  state: isActive ? 'active' : 'inactive',
                })}`}
                {...(clickable
                  ? {
                      role: 'button',
                      tabIndex: 0,
                      'aria-pressed': isActive || undefined,
                      onClick: () => onStepActivate(idx),
                      onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onStepActivate(idx);
                        }
                      },
                    }
                  : {})}
              >
                <div className={stepTextWrapper()}>
                  {item.stepLabel?.value && (
                    <PlainTextWrapper
                      className={stepTextStep({ state: isActive ? 'active' : 'inactive' })}
                      field={item.stepLabel}
                      tag="h6"
                      encode={false}
                      {...getTestProps(`step-label`)}
                    />
                  )}
                  {item.title?.value && (
                    <PlainTextWrapper
                      className={stepTextTitle()}
                      field={item.title}
                      tag="p"
                      encode={false}
                      {...getTestProps(`step-title`)}
                    />
                  )}
                </div>
              </div>

              {!isLast && (
                <Triangle
                  side="right"
                  bg={isActive ? 'bg-color-fill-white' : 'bg-color-fill-light'}
                  data-node-id="tip"
                  withShadow={isActive}
                  className="hidden lg:block"
                />
              )}
            </div>
          );
        })}
      </div>

      {(contents || renderContent) && (
        <div className={contentContainer()}>
          {hasTabs && (
            <div className={tabsContainer()}>
              {stepTabs?.map((tab, tabIndex) => {
                const isActive = tabIndex === activeTabIndex;
                return (
                  <button
                    key={tab.id || tabIndex}
                    id={`tab-${tab.id || tabIndex}`}
                    type="button"
                    className={tabButton({ isActive })}
                    onClick={() => setActiveTabIndex(tabIndex)}
                    role="tab"
                    aria-selected={isActive}
                    aria-controls={`tab-content-${tab.id || tabIndex}`}
                    aria-label={tab.label}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          )}
          <div className={contentPanel({ hasTabs, className: contentClassName })}>
            <div className="p-padding-more">{content}</div>
            {hasTabs && tabsDataForPrint ? (
              // Render PrintFees with all tabs data at once
              <div ref={printContentRef}>
                <PrintFees
                  tabsData={tabsDataForPrint.map((tabData) => ({
                    ...tabData,
                    additionalFeesBlock: tabData.additionalFeesBlock
                      ? {
                          ...tabData.additionalFeesBlock,
                          description: tabData.additionalFeesBlock.description
                            ? {
                                ...tabData.additionalFeesBlock.description,
                                value: stripHtmlTags(tabData.additionalFeesBlock.description.value),
                              }
                            : tabData.additionalFeesBlock.description,
                        }
                      : tabData.additionalFeesBlock,
                  }))}
                  printDescription={printDescription}
                  printLogoIcon={printLogoIcon}
                  printTitle={printTitle}
                />
              </div>
            ) : (
              // Non-tab content
              content &&
              React.isValidElement(content) &&
              (() => {
                const props = content.props as PrintFeesProps;
                const additionalFeesBlock = props?.additionalFeesBlock;
                return (
                  <div ref={printContentRef}>
                    <PrintFees
                      additionalFeesBlock={
                        additionalFeesBlock && additionalFeesBlock.description
                          ? {
                              ...additionalFeesBlock,
                              description: {
                                ...additionalFeesBlock.description,
                                value: stripHtmlTags(additionalFeesBlock.description.value),
                              },
                            }
                          : additionalFeesBlock
                      }
                      step2FeesExclVat={props?.step2FeesExclVat}
                      step2MandatoryFees={props?.step2MandatoryFees}
                      totalObj={props?.totalObj}
                      tabTitle={props?.tabTitle}
                      printDescription={printDescription}
                      printLogoIcon={printLogoIcon}
                      printTitle={printTitle}
                    />
                  </div>
                );
              })()
            )}

            <div className={navBar({ className: nav?.className })}>
              <div className={ctaLeft()}>
                <div className={ctaButtons()}>
                  {!nav?.back?.hidden &&
                  typeof nav?.back?.label === 'function' &&
                  nav?.back?.label({ index: currentIndex })?.value?.hasOwnProperty('href') ? (
                    <LinkWrapper
                      field={nav.back.label({ index: currentIndex }) as LinkField}
                      className="!min-w-[110px]"
                      aria-disabled={nav?.back?.disabled || !canGoPrev || undefined}
                      ctaComponentClass="default"
                      ctaVariant="secondary"
                      onClick={() => {
                        if (nav?.back?.onClick) {
                          nav.back.onClick({ index: currentIndex, go });
                        }
                      }}
                    />
                  ) : (
                    <ButtonWrapper
                      ref={backButtonRef}
                      text={
                        typeof nav?.back?.label === 'function'
                          ? (nav.back.label({ index: currentIndex }) as Field<string>)?.value
                          : ((nav?.back?.label as Field<string>)?.value ?? undefined)
                      }
                      className="!min-w-[110px]"
                      aria-disabled={nav?.back?.disabled || !canGoPrev || undefined}
                      ctaComponentClass="default"
                      ctaVariant="secondary"
                      onClick={() => {
                        if (nav?.back?.onClick) {
                          nav.back.onClick({ index: currentIndex, go });
                        } else if (canGoPrev) {
                          go.prev();
                        }
                        // Scroll to top of content container when previous button is clicked
                        if (stepBlockRef.current) {
                          smoothScrollToElement(stepBlockRef.current as HTMLElement);
                        }
                      }}
                    />
                  )}

                  {!nav?.next?.hidden && (
                    <ButtonWrapper
                      ref={nextButtonRef}
                      text={
                        typeof nav?.next?.label === 'function'
                          ? nav.next.label({ index: currentIndex })?.value
                          : nav?.next?.label?.value
                      }
                      aria-disabled={
                        (typeof nav?.next?.disabled === 'function'
                          ? nav.next.disabled({ index: currentIndex })
                          : nav?.next?.disabled) ||
                        (!canGoNext && !nav?.next?.onClick) ||
                        undefined
                      }
                      disabled={
                        (typeof nav?.next?.disabled === 'function'
                          ? nav.next.disabled({ index: currentIndex })
                          : nav?.next?.disabled) ||
                        (!canGoNext && !nav?.next?.onClick)
                      }
                      ctaComponentClass="default"
                      ctaVariant="primaryOnCanvas"
                      className="!min-w-[212px]"
                      onClick={() => {
                        if (nav?.next?.onClick) {
                          nav.next.onClick({ index: currentIndex, go });
                        } else if (canGoNext) {
                          go.next();
                        }
                        // Scroll to top of content container when next button is clicked
                        if (stepBlockRef.current) {
                          smoothScrollToElement(stepBlockRef.current as HTMLElement);
                        }
                      }}
                    />
                  )}
                </div>
                <div className={ctaText()}>
                  {!ctaDescription?.hidden && (
                    <RichTextWrapper
                      className={ctaInfo()}
                      field={
                        typeof ctaDescription?.label === 'function'
                          ? ctaDescription?.label({ index: currentIndex })
                          : ctaDescription?.label
                      }
                      tag="div"
                      {...getTestProps(`cta-description`)}
                    />
                  )}
                </div>
              </div>
              {(typeof showPrintButton === 'function'
                ? showPrintButton({ index: currentIndex })
                : showPrintButton) && (
                <div className={ctaRight()}>
                  <ButtonWrapper
                    text="Print"
                    className="min-w-auto"
                    ctaComponentClass="default"
                    ctaVariant="tertiary"
                    remixIcon="printer-line"
                    onClick={() => {
                      handlePrint();
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MultiStep;
